import { Device, type Call } from '@twilio/voice-sdk'
import { api } from '../api/client'

/** The browser as a phone.
 *
 *  One Device per signed-in person, created lazily because it asks for the microphone
 *  and nobody wants that prompt on a page they opened to read a list. The Device holds
 *  a short-lived token; Twilio refreshes it before expiry and we hand it a new one.
 *
 *  The call itself carries `call_id` through to Twilio, which posts it to the TwiML app
 *  at /webhook/voice/agent — that is the moment the server dials the borrower, so the
 *  officer is always in the room first and nobody answers to silence. */
let device: Device | null = null
let identity: string | null = null

export type SoftphoneState = 'offline' | 'ready' | 'connecting' | 'on-call' | 'error'

export async function ensureDevice(onState: (s: SoftphoneState, detail?: string) => void) {
  if (device) return device
  const { token, identity: id } = await api.dialerToken()
  identity = id
  const d = new Device(token, {
    // Opus where available: a cold call is hard enough without sounding like a drive-through.
    codecPreferences: ['opus' as never, 'pcmu' as never],
    logLevel: 'error' as never,
  })
  d.on('registered', () => onState('ready'))
  d.on('error', (e: { message?: string }) => onState('error', e?.message))
  d.on('tokenWillExpire', async () => {
    try {
      const fresh = await api.dialerToken()
      d.updateToken(fresh.token)
    } catch {
      onState('error', 'Could not refresh the calling token — reload the page.')
    }
  })
  await d.register()
  device = d
  return d
}

export async function placeCall(callId: number,
                                onState: (s: SoftphoneState, detail?: string) => void): Promise<Call> {
  const d = await ensureDevice(onState)
  onState('connecting')
  const call = await d.connect({ params: { call_id: String(callId) } })
  call.on('accept', () => onState('on-call'))
  call.on('disconnect', () => onState('ready'))
  call.on('cancel', () => onState('ready'))
  call.on('error', (e: { message?: string }) => onState('error', e?.message))
  return call
}

export function hangUp() {
  device?.disconnectAll()
}

export function currentIdentity() {
  return identity
}

/** Tear the Device down — on sign-out, so the microphone is released. */
export function destroy() {
  device?.destroy()
  device = null
  identity = null
}
